/**
 * Palette definitions — swap the primary/accent to reskin the whole app.
 * Each palette lists a base `primary` and `accent` hex plus a `name`.
 * All 10 Tailwind primary shades (50–900) are derived from the base in ./apply.js
 * using HSL interpolation. Add new palettes by appending to this object.
 */

export const PALETTES = {
  'memcoach-current': {
    name: 'MemCoach Original',
    tagline: 'Sky blue + amber — what\'s live today',
    primary: '#0284c7',
    accent:  '#f59e0b',
    paper:   '#f9fafb',
    ink:     '#111827',
  },

  'wine-library': {
    name: 'Wine Library',
    tagline: 'Deep wine + antique gold — library scholar',
    primary: '#6b1e2f',
    accent:  '#c5972b',
    paper:   '#f4ecda',
    ink:     '#1c0808',
  },

  'forest-study': {
    name: 'Forest Study',
    tagline: 'Calm greens on mint cream',
    primary: '#2f5f3d',
    accent:  '#c56b48',
    paper:   '#f0f5ee',
    ink:     '#2d2a1e',
  },

  'corporate-premium': {
    name: 'Corporate Premium',
    tagline: 'Navy + saffron gold',
    primary: '#2e3a5c',
    accent:  '#d99a2e',
    paper:   '#fbf8f2',
    ink:     '#1e1a15',
  },

  'heritage-manuscript': {
    name: 'Heritage Manuscript',
    tagline: 'Vermilion + forest on parchment',
    primary: '#9b2a2a',
    accent:  '#2f5f3d',
    paper:   '#f4ecd8',
    ink:     '#1e1408',
  },

  'midnight-coach': {
    name: 'Midnight Coach',
    tagline: 'Coral + mint on warm black (dark mode)',
    primary: '#ff8a5c',
    accent:  '#7fd8ba',
    paper:   '#141210',
    ink:     '#f2ede2',
    isDark:  true,
  },

  'fresh-focused': {
    name: 'Fresh & Focused',
    tagline: 'Teal + highlighter yellow',
    primary: '#0ea5a4',
    accent:  '#facc15',
    paper:   '#fafaf5',
    ink:     '#1e293b',
  },

  'sunset-kid': {
    name: 'Sunset Kid',
    tagline: 'Violet + warm orange — playful',
    primary: '#7c3aed',
    accent:  '#f97316',
    paper:   '#fef9f0',
    ink:     '#292524',
  },

  'study-salon': {
    name: 'Study Salon',
    tagline: 'Aubergine + peach — warm boutique',
    primary: '#5a2244',
    accent:  '#e8886a',
    paper:   '#fdf6f0',
    ink:     '#2a1f26',
  },

  'ocean-depth': {
    name: 'Ocean Depth',
    tagline: 'Deep teal + coral',
    primary: '#0e4d64',
    accent:  '#ff7f50',
    paper:   '#f3f7f9',
    ink:     '#0f172a',
  },

  'notebook-classic': {
    name: 'Notebook Classic',
    tagline: 'Navy + highlighter yellow',
    primary: '#1e3a5f',
    accent:  '#facc15',
    paper:   '#fbf8f0',
    ink:     '#1c1917',
  },

  'rose-petal': {
    name: 'Rose Petal',
    tagline: 'Rose garnet + dark olive',
    primary: '#b0384f',
    accent:  '#3d4a2c',
    paper:   '#fbf3f2',
    ink:     '#1c1112',
  },

  'terracotta-sky': {
    name: 'Terracotta Sky',
    tagline: 'Terracotta + dusty sky blue',
    primary: '#c96a4d',
    accent:  '#4a7fb0',
    paper:   '#fef7f0',
    ink:     '#26170f',
  },

  'blush-boutique': {
    name: 'Blush Boutique',
    tagline: 'Deep pine + blush rose',
    primary: '#234a35',
    accent:  '#d97b8e',
    paper:   '#f9f0ef',
    ink:     '#1c1817',
  },

  'mint-fresh': {
    name: 'Mint Fresh',
    tagline: 'Deep mint + coral',
    primary: '#2c8478',
    accent:  '#f4805a',
    paper:   '#f0f7f4',
    ink:     '#14201c',
  },

  'copper-nights': {
    name: 'Copper Nights',
    tagline: 'Steel navy + aged copper',
    primary: '#263b52',
    accent:  '#b57848',
    paper:   '#f3ede4',
    ink:     '#10161d',
  },

  'slate-study': {
    name: 'Slate Study',
    tagline: 'Slate grey + mustard',
    primary: '#37424f',
    accent:  '#d4a838',
    paper:   '#f2ede0',
    ink:     '#131a24',
  },

  'lavender-lab': {
    name: 'Lavender Lab',
    tagline: 'Muted lavender + amber',
    primary: '#6b4c93',
    accent:  '#e0a340',
    paper:   '#f6f2f8',
    ink:     '#241a30',
  },

  'rust-retreat': {
    name: 'Rust Retreat',
    tagline: 'Rust + peacock teal — earthy',
    primary: '#a04728',
    accent:  '#2a7a70',
    paper:   '#f8ede0',
    ink:     '#241408',
  },

  'berry-bloom': {
    name: 'Berry Bloom',
    tagline: 'Berry + soft mint',
    primary: '#872b5c',
    accent:  '#6bbaa0',
    paper:   '#f7ecf1',
    ink:     '#1e0e18',
  },

  'emerald-cream': {
    name: 'Emerald Cream',
    tagline: 'Emerald + persimmon',
    primary: '#1e6b47',
    accent:  '#d97442',
    paper:   '#f7f3e8',
    ink:     '#14201c',
  },

  // ============ NEW COMBINATIONS ============

  'chalkboard': {
    name: 'Chalkboard',
    tagline: 'Deep green + chalk white — classroom nostalgia',
    primary: '#1a3a2e',
    accent:  '#e8b04b',
    paper:   '#fafaf7',
    ink:     '#0f1f19',
  },

  'espresso-cream': {
    name: 'Espresso Cream',
    tagline: 'Deep coffee + latte foam',
    primary: '#4a2c1e',
    accent:  '#c98846',
    paper:   '#faf3ea',
    ink:     '#2a1810',
  },

  'peacock-plum': {
    name: 'Peacock Plum',
    tagline: 'Vivid teal + magenta accent',
    primary: '#0f6470',
    accent:  '#c2417a',
    paper:   '#f4f8f9',
    ink:     '#0d1f22',
  },

  'denim-rose': {
    name: 'Denim & Rose',
    tagline: 'Denim indigo + dusty rose',
    primary: '#39568c',
    accent:  '#d1859a',
    paper:   '#f4f4f7',
    ink:     '#1a2035',
  },

  'sunrise-mustard': {
    name: 'Sunrise Mustard',
    tagline: 'Mustard yellow + deep navy',
    primary: '#1e2a4a',
    accent:  '#e0a832',
    paper:   '#fdfaf0',
    ink:     '#0f1428',
  },

  'aqua-sand': {
    name: 'Aqua Sand',
    tagline: 'Turquoise + warm tan',
    primary: '#1a8a8a',
    accent:  '#c99a5c',
    paper:   '#f5f4ec',
    ink:     '#0e2828',
  },

  'graphite-lime': {
    name: 'Graphite Lime',
    tagline: 'Charcoal grey + electric lime',
    primary: '#2a2f36',
    accent:  '#a3d63a',
    paper:   '#f4f4f2',
    ink:     '#0d1015',
  },

  'sunset-brick': {
    name: 'Sunset Brick',
    tagline: 'Burnt sienna + navy — study lounge',
    primary: '#8a3a1e',
    accent:  '#2b3f5c',
    paper:   '#fbf3ea',
    ink:     '#280f05',
  },

  'sky-coral-bright': {
    name: 'Sky Coral',
    tagline: 'Sky blue + coral — bright & modern',
    primary: '#0891b2',
    accent:  '#f16b58',
    paper:   '#f5fafd',
    ink:     '#0c2530',
  },

  'olive-blush': {
    name: 'Olive Blush',
    tagline: 'Olive green + blush pink — earthy soft',
    primary: '#556b2f',
    accent:  '#e89aa4',
    paper:   '#f7f5eb',
    ink:     '#1f2812',
  },

  // ============ CURATED SET ============

  'nordic-frost': {
    name: 'Nordic Frost',
    tagline: 'Steel blue + coral — quiet, considered, Scandinavian',
    primary: '#3d5a80',
    accent:  '#ee6c4d',
    paper:   '#eef2f6',
    ink:     '#1e2a3a',
  },

  'editorial-magazine': {
    name: 'Editorial',
    tagline: 'Near-black + terracotta — long-form magazine',
    primary: '#2d2925',
    accent:  '#b8613a',
    paper:   '#f5efe5',
    ink:     '#171410',
  },

  'botanical-ink': {
    name: 'Botanical',
    tagline: 'Deep forest + burnt orange — herbarium plate',
    primary: '#2f4a2e',
    accent:  '#d4761f',
    paper:   '#f7f4ec',
    ink:     '#0f1a10',
  },

  'miami-deco': {
    name: 'Miami Deco',
    tagline: 'Teal + hot pink on beige — bold art deco',
    primary: '#007c8f',
    accent:  '#e94991',
    paper:   '#faf0e0',
    ink:     '#0a2530',
  },

  'tokyo-metro': {
    name: 'Tokyo Metro',
    tagline: 'Inky navy + neon yellow — signage sharp',
    primary: '#12203f',
    accent:  '#f5d10a',
    paper:   '#f6f2e8',
    ink:     '#050914',
  },

  'kraft-journal': {
    name: 'Kraft Journal',
    tagline: 'Burnt umber + sage on kraft — hand-bound',
    primary: '#6b4423',
    accent:  '#8a9a5b',
    paper:   '#efe4ce',
    ink:     '#241408',
  },

  'amber-vault': {
    name: 'Amber Vault',
    tagline: 'Walnut + amber gold — whiskey library',
    primary: '#4a2f1a',
    accent:  '#d4a054',
    paper:   '#f5ebd7',
    ink:     '#1a0f05',
  },

  'neon-twilight': {
    name: 'Neon Twilight',
    tagline: 'Lavender + magenta on ink — dark mode',
    primary: '#b980ff',
    accent:  '#ff3ea5',
    paper:   '#1a1524',
    ink:     '#f5f2ff',
    isDark:  true,
  },

  'concrete-rose': {
    name: 'Concrete & Rose',
    tagline: 'Concrete grey + dusty rose — brutalist soft',
    primary: '#52544f',
    accent:  '#cc7e6f',
    paper:   '#efece6',
    ink:     '#181812',
  },

  'oxblood-cream': {
    name: 'Oxblood',
    tagline: 'Oxblood red + antique cream — bookbinder',
    primary: '#5d1f24',
    accent:  '#c9a95c',
    paper:   '#f3e9d4',
    ink:     '#1a0808',
  },
};

export const DEFAULT_PALETTE_ID = 'memcoach-current';

export function getPalette(id) {
  return PALETTES[id] || PALETTES[DEFAULT_PALETTE_ID];
}
